/* eslint-disable no-unused-vars */
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./Home.css";
import { Link } from "react-router-dom";
import MainTitle from "../../components/title/mainTitle";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { toast } from "react-toastify";
import axios from "axios";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import Threads from "../../components/Threads/Threads";
import Seo from "../../components/Seo/Seo";
import {
  CONTACT_DIAL_OPTIONS,
  ContactDialFlagImg,
  dialMetaForCode,
} from "../../utils/phoneDialCountries";

/** Stable RGB (0–1) for WebGL Threads — avoids effect re-running every render */
const HERO_THREADS_COLOR = Object.freeze([1, 0.96, 0.9]);

// Helper to rewrite absolute API-hosted URLs to the current axios base host during development.
const apiHost = axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, "") : "";
const resolveUrl = (url) => {
  if (!url) return url;
  try {
    if (url.startsWith("/uploads/") && apiHost) {
      return `${apiHost}${url}`;
    }

    if (url.startsWith("http://") || url.startsWith("https://")) {
      // If we have an apiHost configured, replace the origin of the absolute URL
      // with the apiHost so resources resolve against our dev server.
      const parsed = new URL(url);
      if (apiHost) {
        return url.replace(parsed.origin, apiHost);
      }
      // If no apiHost (usually axios baseURL is '/api'), strip the origin to make
      // the URL relative (so the browser requests the same host as the app).
      return url.replace(parsed.origin, "");
    }
    return url;
  } catch (e) {
    return url;
  }
};

/** Browser rejects <link rel=preload as=image> for empty/invalid href — avoid console noise */
function isSafeImagePreloadHref(href) {
  if (typeof href !== "string") return false;
  const u = href.trim();
  if (!u) return false;
  if (u === "undefined" || u === "null") return false;
  return (
    u.startsWith("/") ||
    u.startsWith("http://") ||
    u.startsWith("https://") ||
    u.startsWith("data:image/")
  );
}

/** Last known hero image URL from CMS — restores instant LCP on reload before /content/home returns */
const HOME_HERO_IMAGE_URL_KEY = "alramlaa_home_hero_image_url_v1";

function readCachedHeroImageUrl() {
  if (typeof window === "undefined") return null;
  try {
    const v = sessionStorage.getItem(HOME_HERO_IMAGE_URL_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

function Home() {
  const [homeData, setHomeData] = useState(null);
  const [cachedHeroImageUrl] = useState(readCachedHeroImageUrl);
  const [t] = useTranslation("global");

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await axios.get("/content/home");
        const data = response?.data;
        setHomeData(data);
        try {
          const heroUrl = data?.heroImage?.url;
          if (heroUrl) sessionStorage.setItem(HOME_HERO_IMAGE_URL_KEY, heroUrl);
          else sessionStorage.removeItem(HOME_HERO_IMAGE_URL_KEY);
        } catch {
          /* ignore quota / private mode */
        }
      } catch (error) {
        toast.error("Failed to fetch data. Please try again later.");
      }
    };
    fetchHomeData();
  }, [t]);

  const heroImageUrlRaw = homeData?.heroImage?.url || cachedHeroImageUrl;

  return (
    <>
      <Seo
        description="الرملاء للصناعات الخشبية — تصنيع وتصميم أثاث ومنتجات خشبية عالية الجودة في المملكة العربية السعودية."
        path="/"
      />
      <main className="home">
        <HeroSection homeData={homeData} heroImageUrlRaw={heroImageUrlRaw} />
        <AboutSection homeData={homeData} />
        <Projects />
        <Testimonial />
        <OurParteners />
        <Contact />
      </main>
    </>
  );
}

function HeroSection({ homeData, heroImageUrlRaw }) {
  const heroPatternId = `hero-grid-${useId().replace(/:/g, "")}`;
  const [t] = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);
  const heroSrc = heroImageUrlRaw ? resolveUrl(heroImageUrlRaw) : null;

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const id = "alramlaa-hero-image-preload";
    const existing = document.getElementById(id);
    if (!heroSrc || !isSafeImagePreloadHref(heroSrc)) {
      if (existing) existing.remove();
      return;
    }
    let link = existing;
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "preload";
      link.as = "image";
      link.setAttribute("fetchpriority", "high");
      document.head.appendChild(link);
    }
    link.href = heroSrc;
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [heroSrc]);

  return (
    <section className="hero hero--premium">
      <div className="hero-media" aria-hidden="true">
        {heroSrc ? (
          <img
            src={heroSrc}
            alt=""
            fetchPriority="high"
          />
        ) : (
          <div className="hero-media-fallback" />
        )}
        <div className="hero-threads" aria-hidden="true">
          <Threads
            color={HERO_THREADS_COLOR}
            amplitude={2.1}
            distance={0.3}
            enableMouseInteraction={false}
          />
        </div>
        <div className="hero-gradient" />
        <svg className="hero-pattern" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id={heroPatternId} width="48" height="48" patternUnits="userSpaceOnUse">
              <path
                d="M 48 0 L 0 0 0 48"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#${heroPatternId})`} />
          <g fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.35">
            <path d="M80 620 L420 180 L920 520 L1120 120" />
            <path d="M0 480 L280 320 L640 680 L1200 400" />
          </g>
        </svg>
      </div>
      <div className="container hero-inner">
        <div className="title" data-aos="fade-down" data-aos-duration="1000">
          <h1>
            {(lang === `"ar"` ? homeData?.arMainText : homeData?.mainText)?.trim() ||
              t("hero.titleFallback")}
          </h1>
        </div>
        <div className="description" data-aos="fade-up" data-aos-duration="800">
          <p>
            {(lang === `"ar"` ? homeData?.arHeroDescription : homeData?.heroDescription)?.trim() ||
              t("hero.descFallback")}
          </p>
        </div>
        <div className="hero-cta-row" data-aos="fade-up" data-aos-duration="900">
          <Link className="hero-cta hero-cta--primary" to="/projects">
            {t("hero.ctaPrimary")}
          </Link>
          <Link className="hero-cta hero-cta--secondary" to="/contact">
            {t("hero.ctaSecondary")}
          </Link>
        </div>
      </div>
    </section>
  );
}

/** About cards + Projects grid: carousel below this width (less vertical scroll, stable RTL) */
const HOME_PREMIUM_MOBILE_MQ = "(max-width: 600px)";

function AboutSection({ homeData }) {
  const [boxes, setBoxes] = useState([]);
  const [t] = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);
  const [aboutBoxesAsSwiper, setAboutBoxesAsSwiper] = useState(
    () => typeof window !== "undefined" && window.matchMedia(HOME_PREMIUM_MOBILE_MQ).matches
  );
  const [aboutBoxMinHeightPx, setAboutBoxMinHeightPx] = useState(undefined);
  const aboutSwiperRef = useRef(null);

  useEffect(() => {
    const fetchBoxes = async () => {
      try {
        const response = await axios.get("/content/boxes");
        setBoxes(response?.data);
      } catch (error) {}
    };

    fetchBoxes();

    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(HOME_PREMIUM_MOBILE_MQ);
    const sync = () => setAboutBoxesAsSwiper(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useLayoutEffect(() => {
    if (!aboutBoxesAsSwiper || !boxes?.length) {
      setAboutBoxMinHeightPx(undefined);
      return undefined;
    }

    const rootSelector = ".home .about.about--premium .container .info .boxs.boxs--swiper.swiper";

    const measureAndSync = () => {
      const root = document.querySelector(rootSelector);
      if (!root) return;
      const boxEls = root.querySelectorAll(".swiper-slide .box");
      if (!boxEls.length) return;
      let max = 0;
      boxEls.forEach((node) => {
        const prev = node.style.minHeight;
        node.style.minHeight = "";
        max = Math.max(max, node.offsetHeight);
        node.style.minHeight = prev;
      });
      if (max > 0) {
        setAboutBoxMinHeightPx(Math.ceil(max));
        queueMicrotask(() => aboutSwiperRef.current?.update?.());
      }
    };

    measureAndSync();
    const raf = requestAnimationFrame(measureAndSync);
    const root = document.querySelector(rootSelector);
    const ro = root ? new ResizeObserver(measureAndSync) : null;
    if (root) ro.observe(root);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [aboutBoxesAsSwiper, boxes, lang]);

  useEffect(() => {
    if (aboutBoxMinHeightPx) aboutSwiperRef.current?.update?.();
  }, [aboutBoxMinHeightPx]);

  return (
    <section className="about about--premium">
      <div className="container about-premium-inner">
        <div
          className="info"
          data-aos={lang === `"en"` ? "fade-right" : "fade-left"}
          data-aos-duration="1200"
        >
          <span className="about-eyebrow">{t("aboutus.eyebrow")}</span>
          <MainTitle>{t("header.about")}</MainTitle>
          <p className="about-lead">
            {lang === `"ar"`
              ? homeData?.arAboutDescription
              : homeData?.aboutDescription}
          </p>
          {aboutBoxesAsSwiper ? (
            <Swiper
              key={lang === `"ar"` ? "about-boxes-rtl" : "about-boxes-ltr"}
              className="boxs boxs--swiper"
              modules={[Pagination]}
              pagination={{ clickable: true, dynamicBullets: true }}
              spaceBetween={0}
              slidesPerView={1}
              centeredSlides
              slidesOffsetBefore={0}
              slidesOffsetAfter={0}
              dir={lang === `"ar"` ? "rtl" : "ltr"}
              watchOverflow
              onSwiper={(instance) => {
                aboutSwiperRef.current = instance;
              }}
            >
              {boxes?.map((box, index) => (
                <SwiperSlide key={box?._id}>
                  <div
                    className="box"
                    style={
                      aboutBoxMinHeightPx
                        ? { minHeight: `${aboutBoxMinHeightPx}px` }
                        : undefined
                    }
                  >
                    <h3 className="box-title">{lang === `"ar"` ? box?.arBoxTitle : box?.boxTitle}</h3>
                    <p className="box-text">{lang === `"ar"` ? box?.arBoxText : box?.boxText}</p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div
              className="boxs"
              data-aos={lang === `"en"` ? "fade-right" : "fade-left"}
              data-aos-duration="1400"
            >
              {boxes?.map((box, index) => (
                <div className="box" key={box?._id}>
                  <h3 className="box-title">{lang === `"ar"` ? box?.arBoxTitle : box?.boxTitle}</h3>
                  <p className="box-text">{lang === `"ar"` ? box?.arBoxText : box?.boxText}</p>
                </div>
              ))}
            </div>
          )}
          <div className="about-premium-actions">
            <Link to="/about" className="readMore">
              {t("aboutus.readMore")}
            </Link>
            <Link to="/store" className="readMore readMore--ghost">
              {t("aboutus.browseStore")}
            </Link>
          </div>
        </div>
        <div
          className="imgs imgs--masonry"
          data-aos={lang === `"en"` ? "fade-left" : "fade-right"}
          data-aos-duration="1400"
        >
          <div className="about-masonry-col about-masonry-col--start">
            <figure className="about-photo">
              <img
                src={resolveUrl(homeData?.aboutImages[0]?.url)}
                alt={homeData?.aboutImages[0]?.alt}
                loading="lazy"
                decoding="async"
              />
            </figure>
            <figure className="about-photo">
              <img
                src={resolveUrl(homeData?.aboutImages[1]?.url)}
                alt={homeData?.aboutImages[1]?.alt}
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
          <div className="about-masonry-col about-masonry-col--end">
            <figure className="about-photo">
              <img
                src={resolveUrl(homeData?.aboutImages[2]?.url)}
                alt={homeData?.aboutImages[2]?.alt}
                loading="lazy"
                decoding="async"
              />
            </figure>
            <figure className="about-photo">
              <img
                src={resolveUrl(homeData?.aboutImages[3]?.url)}
                alt={homeData?.aboutImages[3]?.alt}
                loading="lazy"
                decoding="async"
              />
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

function Projects() {
  const { t, i18n } = useTranslation("global");
  const isAr = i18n.language.startsWith("ar");
  const [projectsAsSwiper, setProjectsAsSwiper] = useState(
    () => typeof window !== "undefined" && window.matchMedia(HOME_PREMIUM_MOBILE_MQ).matches
  );
  const [projectsCardMinHeightPx, setProjectsCardMinHeightPx] = useState(undefined);
  const projectsSwiperRef = useRef(null);
  const [categoryThumbs, setCategoryThumbs] = useState(() => ({}));

  useEffect(() => {
    const mq = window.matchMedia(HOME_PREMIUM_MOBILE_MQ);
    const sync = () => setProjectsAsSwiper(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchCategoryThumbs = async () => {
      try {
        // Pull a small batch and use the first image per category as a thumbnail.
        const response = await axios.get("/projects?page=1");
        if (cancelled) return;
        const batch = response?.data?.projects;
        const projects = Array.isArray(batch) ? batch : [];
        if (!projects.length) {
          setCategoryThumbs({});
          return;
        }

        const next = {};
        projects.forEach((p) => {
          const key = typeof p?.category === "string" ? p.category.trim() : "";
          const imgUrl = p?.image?.url ? String(p.image.url).trim() : "";
          if (!key || !imgUrl) return;
          if (next[key]) return;
          next[key] = resolveUrl(imgUrl);
        });

        setCategoryThumbs(next);
      } catch {
        // Keep defaults on error.
        if (!cancelled) setCategoryThumbs({});
      }
    };

    fetchCategoryThumbs();
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    if (!projectsAsSwiper) {
      setProjectsCardMinHeightPx(undefined);
      return undefined;
    }

    const rootSelector =
      ".home .projects-section.projects-section--premium .projects-premium-grid.projects-premium-grid--swiper.swiper";

    const measureAndSync = () => {
      const root = document.querySelector(rootSelector);
      if (!root) return;
      const cardEls = root.querySelectorAll(".swiper-slide .project-card");
      if (!cardEls.length) return;
      let max = 0;
      cardEls.forEach((node) => {
        const prev = node.style.minHeight;
        node.style.minHeight = "";
        max = Math.max(max, node.offsetHeight);
        node.style.minHeight = prev;
      });
      if (max > 0) {
        setProjectsCardMinHeightPx(Math.ceil(max));
        queueMicrotask(() => projectsSwiperRef.current?.update?.());
      }
    };

    measureAndSync();
    const raf = requestAnimationFrame(measureAndSync);
    const root = document.querySelector(rootSelector);
    const ro = root ? new ResizeObserver(measureAndSync) : null;
    if (root) ro.observe(root);

    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [projectsAsSwiper, i18n.language]);

  useEffect(() => {
    if (projectsCardMinHeightPx) projectsSwiperRef.current?.update?.();
  }, [projectsCardMinHeightPx]);

  const categories = [
    {
      key: "Hospitality",
      arKey: "ضيافة",
      defaultSrc: "/photos/hosipilty.webp",
      title: t("projects.hospitality"),
    },
    {
      key: "Commercial",
      arKey: "تجاري",
      defaultSrc: "/photos/commmercial.webp",
      title: t("projects.commercial"),
    },
    {
      key: "Entertainment",
      arKey: "ترفيه",
      defaultSrc: "/photos/entertaiment.webp",
      title: t("projects.entertainment"),
    },
    {
      key: "Residential",
      arKey: "سكني",
      defaultSrc: "/photos/residential.webp",
      title: t("projects.residential"),
    },
    {
      key: "Education",
      arKey: "تعليم",
      defaultSrc: "/photos/education.webp",
      title: t("projects.education"),
    },
    {
      key: "Work Space",
      arKey: "مساحة العمل",
      defaultSrc: "/photos/workspacee.webp",
      title: t("projects.workspace"),
    },
  ];

  return (
    <section className="projects-section projects-section--premium">
      <div className="container projects-premium-inner">
        <header className="projects-premium-head">
          <span className="projects-eyebrow">{t("projects.eyebrow")}</span>
          <MainTitle>{t("projects.title")}</MainTitle>
        </header>
        {projectsAsSwiper ? (
          <Swiper
            key={isAr ? "projects-premium-rtl" : "projects-premium-ltr"}
            className="projects-premium-grid projects-premium-grid--swiper"
            modules={[Pagination]}
            pagination={{ clickable: true, dynamicBullets: true }}
            spaceBetween={0}
            slidesPerView={1}
            centeredSlides
            dir={isAr ? "rtl" : "ltr"}
            watchOverflow
            onSwiper={(instance) => {
              projectsSwiperRef.current = instance;
            }}
          >
            {categories.map((cat) => {
              const hrefCategory = isAr ? cat.arKey : cat.key;
              const route = `/projects/${encodeURIComponent(hrefCategory)}`;
              const apiThumb = categoryThumbs[cat.key];
              const imgSrc = apiThumb || `${process.env.PUBLIC_URL}${cat.defaultSrc}`;
              return (
              <SwiperSlide key={cat.key}>
                <Link
                  to={route}
                  className="project-card"
                  style={
                    projectsCardMinHeightPx
                      ? { minHeight: `${projectsCardMinHeightPx}px` }
                      : undefined
                  }
                >
                  <div className="project-card-media">
                    <img
                      src={imgSrc}
                      alt={cat.title}
                      loading="lazy"
                    />
                    <div className="project-card-overlay" aria-hidden="true" />
                    <div className="project-card-footer">
                      <span className="project-card-title">{cat.title}</span>
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
              );
            })}
          </Swiper>
        ) : (
          <div className="projects-premium-grid">
            {categories.map((cat, index) => {
              const hrefCategory = isAr ? cat.arKey : cat.key;
              const route = `/projects/${encodeURIComponent(hrefCategory)}`;
              const apiThumb = categoryThumbs[cat.key];
              const imgSrc = apiThumb || `${process.env.PUBLIC_URL}${cat.defaultSrc}`;
              return (
              <Link
                key={cat.key}
                to={route}
                className="project-card"
                data-aos="fade-up"
                data-aos-duration="750"
                data-aos-delay={index * 70}
              >
                <div className="project-card-media">
                  <img
                    src={imgSrc}
                    alt={cat.title}
                    loading="lazy"
                  />
                  <div className="project-card-overlay" aria-hidden="true" />
                  <div className="project-card-footer">
                    <span className="project-card-title">{cat.title}</span>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
        <div className="projects-premium-cta-wrap">
          <Link to="/projects" className="projects-cta">
            {t("projects.seeAll")}
          </Link>
        </div>
      </div>
    </section>
  );
}

const TESTIMONIAL_FALLBACK_SLIDES = [
  { key: "d1", photo: "/photos/residential.webp" },
  { key: "d2", photo: "/photos/hosipilty.webp" },
  { key: "d3", photo: "/photos/commmercial.webp" },
];

function Testimonial() {
  const [t] = useTranslation("global");
  return (
    <section className="testimonials testimonials--premium">
      <div className="container testimonials-premium-inner">
        <header className="testimonials-premium-head">
          <span className="testimonials-eyebrow">{t("testimonials.eyebrow")}</span>
          <MainTitle>{t("testimonials.title")}</MainTitle>
          <p className="testimonials-lead">{t("testimonials.subtitle")}</p>
        </header>
        <div className="testimonials-panel">
          <TestimonialSlider />
        </div>
      </div>
    </section>
  );
}

const TestimonialSlider = () => {
  const { t, i18n } = useTranslation("global");
  const [apiReviews, setApiReviews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/reviews");
        const raw = response?.data?.reviews;
        setApiReviews(Array.isArray(raw) ? raw : []);
      } catch {
        setApiReviews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const slides = useMemo(() => {
    if (loading || apiReviews === null) return [];
    if (apiReviews.length > 0) {
      return apiReviews.map((review, i) => ({
        kind: "api",
        key: String(review?._id ?? i),
        review,
      }));
    }
    return TESTIMONIAL_FALLBACK_SLIDES.map((row) => ({
      kind: "fallback",
      key: row.key,
      photo: row.photo,
    }));
  }, [loading, apiReviews]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [apiReviews, i18n.language]);

  useEffect(() => {
    if (slides.length && currentIndex >= slides.length) {
      setCurrentIndex(0);
    }
  }, [slides.length, currentIndex]);

  if (loading) {
    return (
      <div className="testimonial-loader" aria-busy="true">
        <BeatLoader color="#c9a45c" size={12} />
      </div>
    );
  }

  if (!slides.length) {
    return null;
  }

  const isAr = i18n.language.startsWith("ar");
  const dirClass = isAr ? "is-rtl" : "is-ltr";
  const n = slides.length;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % n);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? n - 1 : prev - 1));
  };

  const placeholderImg = `${process.env.PUBLIC_URL}${TESTIMONIAL_FALLBACK_SLIDES[0].photo}`;

  return (
    <div className="testimonial-shell">
      <div
        className={`testimonial-track ${dirClass}`}
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        aria-live="polite"
      >
        {slides.map((item) => {
          const title =
            item.kind === "api"
              ? isAr
                ? item.review?.arTitle
                : item.review?.title
              : t(`testimonials.defaults.${item.key}.title`);
          const quote =
            item.kind === "api"
              ? isAr
                ? item.review?.arDescription
                : item.review?.description
              : t(`testimonials.defaults.${item.key}.quote`);
          const name =
            item.kind === "api"
              ? isAr
                ? item.review?.arName
                : item.review?.name
              : t(`testimonials.defaults.${item.key}.name`);
          const imgSrc =
            item.kind === "api"
              ? resolveUrl(item.review?.image?.url) || placeholderImg
              : `${process.env.PUBLIC_URL}${item.photo}`;
          const imgAlt =
            item.kind === "api"
              ? isAr
                ? item.review?.image?.arAlt || title
                : item.review?.image?.alt || title
              : title;

          return (
            <article key={item.key} className="testimonial-slide">
              <div className="testimonial-slide-photo">
                <img src={imgSrc || placeholderImg} alt={imgAlt || ""} loading="lazy" />
              </div>
              <div className="testimonial-slide-body">
                <span className="testimonial-quote-mark" aria-hidden="true">
                  “
                </span>
                <h3 className="testimonial-slide-title">{title}</h3>
                <p className="testimonial-quote">{quote}</p>
                <footer className="testimonial-attribution">
                  <cite className="testimonial-name">{name}</cite>
                </footer>
              </div>
            </article>
          );
        })}
      </div>
      <div className={`testimonial-nav ${dirClass}`}>
        <button
          type="button"
          className="testimonial-nav-btn"
          onClick={handlePrev}
          aria-label={t("testimonials.prev")}
        >
          ‹
        </button>
        <button
          type="button"
          className="testimonial-nav-btn"
          onClick={handleNext}
          aria-label={t("testimonials.next")}
        >
          ›
        </button>
      </div>
    </div>
  );
};

function OurParteners() {
  const [t] = useTranslation("global");

  return (
    <section className="our-parteners our-parteners--premium">
      <div className="container partners-premium-inner">
        <header className="partners-premium-head">
          <span className="partners-eyebrow">{t("partners.eyebrow")}</span>
          <MainTitle>{t("partners.title")}</MainTitle>
          <p className="partners-lead">{t("partners.subtitle")}</p>
        </header>
        <SwiperSliderParteners />
      </div>
    </section>
  );
}

const SwiperSliderParteners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [t, i18n] = useTranslation("global");

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/partners");
        setPartners(response?.data);
      } catch (error) {
        console.error("Error fetching partners:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  if (loading) {
    return (
      <div className="loader-container" aria-busy="true">
        <BeatLoader />
      </div>
    );
  }

  if (!partners?.length) {
    return (
      <p className="partners-premium-empty" role="status">
        {t("partners.empty")}
      </p>
    );
  }

  return (
    <Swiper
      key={`partners-${i18n.language}-${partners.length}`}
      dir={i18n.dir()}
      spaceBetween={20}
      loop={partners.length > 4}
      autoplay={{
        delay: 3200,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }}
      modules={[Autoplay, Pagination, Navigation]}
      slidesPerView={8}
      breakpoints={{
        100: {
          slidesPerView: 2,
          spaceBetween: 10,
        },
        768: {
          slidesPerView: 4,
          spaceBetween: 15,
        },
        1024: {
          slidesPerView: 5,
          spaceBetween: 20,
        },
        1280: {
          slidesPerView: 6,
          spaceBetween: 20,
        },
        1440: {
          slidesPerView: 7,
          spaceBetween: 22,
        },
        1800: {
          slidesPerView: 8,
          spaceBetween: 24,
        },
      }}
    >
      {partners?.map((partner) => (
        <SwiperSlide key={partner?._id}>
          <div className="partener">
              <div className="img-box">
              <img src={resolveUrl(partner?.image?.url)} alt={partner?.image?.alt} />
            </div>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export function Contact({ variant = "home" }) {
  const isContactPage = variant === "page";
  const dialListId = useId();
  const dialWrapRef = useRef(null);
  const [phoneDialOpen, setPhoneDialOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState("+966");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [contact, setContact] = useState({});
  const { t, i18n } = useTranslation("global");
  const [lang, setLang] = useState(localStorage.getItem("lang") || `"ar"`);
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar") || lang === `"ar"`;

  useEffect(() => {
    const getContent = async () => {
      try {
        const response = await axios.get("/content");
        setContact(response?.data.contact);
      } catch (error) {
        toast.error(t("popups.error"));
      }
    };

    getContent();

    const handleStorageChange = () => {
      setLang(localStorage.getItem("lang") || `"ar"`);
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [t]);

  useEffect(() => {
    if (!phoneDialOpen) return;
    const onDocMouseDown = (e) => {
      if (dialWrapRef.current && !dialWrapRef.current.contains(e.target)) {
        setPhoneDialOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setPhoneDialOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [phoneDialOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!name?.trim() || !email?.trim() || !subject?.trim() || !message?.trim() || !consent) {
      return toast.error(t("popups.fields"));
    }

    const phoneDigits = phone.replace(/\D/g, "").replace(/^0+/, "");
    if (phoneDigits.length < 8) {
      return toast.error(t("contact.phoneInvalid"));
    }

    const messageData = {
      name: name.trim(),
      email: email.trim(),
      phone: `${phoneDialCode} ${phoneDigits}`,
      message: `${t("contact.subjectPrefix")}: ${subject.trim()}\n\n${message.trim()}`,
    };

    try {
      setLoading(true);
      const response = await toast.promise(
        axios.post("/messages", messageData, {
          headers: {
            "Content-Type": "application/json",
          },
        }),
        {
          pending: t("popups.pending"),
        }
      );

      setName("");
      setEmail("");
      setPhoneDialCode("+966");
      setPhoneDialOpen(false);
      setPhone("");
      setSubject("");
      setMessage("");
      setConsent(false);

      setShowConfirmation(true);

      setTimeout(() => {
        setShowConfirmation(false);
      }, 2500);
    } catch (error) {
      const errorMessage = t("popups.error");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section
        className={`contact contact--modern${isContactPage ? " contact--page" : ""}`}
        dir={isRtl ? "rtl" : "ltr"}
        lang={isRtl ? "ar" : "en"}
      >
        <div className="container">
          {!isContactPage && (
            <div className="contact-modern-heading">
              <h2 className="contact-modern-heading-title">
                {lang === `"ar"`
                  ? contact?.arSectionTitle || t("contact.title")
                  : contact?.sectionTitle || t("contact.title")}
              </h2>
              {(lang === `"ar"` ? contact?.arSubText : contact?.subText) && (
                <p className="contact-modern-heading-sub">
                  {lang === `"ar"` ? contact.arSubText : contact.subText}
                </p>
              )}
            </div>
          )}
          <div className="contact-modern-inner">
            <div className="contact-modern-info">
              <span className="contact-modern-eyebrow">
                {t("contact.modernEyebrow")}
              </span>
              <h2 className="contact-modern-title">
                <span className="contact-modern-title-line">
                  {t("contact.headline1")}
                </span>
                <span className="contact-modern-title-line">
                  {t("contact.headline2")}
                </span>
              </h2>
              <div className="contact-modern-channels">
                <div className="contact-modern-channel">
                  <h3>{t("contact.channelInfoLabel")}</h3>
                  <a href={`mailto:${t("contact.infoEmail")}`}>
                    <i className="fas fa-envelope" aria-hidden />
                    {t("contact.infoEmail")}
                  </a>
                </div>
              </div>
            </div>
            <div className="contact-modern-form-wrap">
              {(lang === `"ar"` ? contact?.arDescription : contact?.description) ? (
                <p className="contact-modern-form-lead">
                  {lang === `"ar"` ? contact.arDescription : contact.description}
                </p>
              ) : null}
              <form className="contact-modern-form" onSubmit={handleSendMessage}>
                <div className="contact-modern-grid">
                  <label className="contact-modern-field">
                    <i className="fas fa-user" aria-hidden />
                    <input
                      type="text"
                      name="name"
                      autoComplete="name"
                      placeholder={t("contact.name")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </label>
                  <label className="contact-modern-field">
                    <i className="fas fa-envelope" aria-hidden />
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder={t("contact.emailPlaceholder")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </label>
                  <label className="contact-modern-field contact-modern-field--phone">
                    <i className="fas fa-phone" aria-hidden />
                    <span className="contact-modern-phone-inputs">
                      <div className="contact-modern-dial-wrap" ref={dialWrapRef}>
                        <button
                          type="button"
                          className="contact-modern-dial-trigger"
                          aria-label={t("contact.countryCodeAria")}
                          aria-expanded={phoneDialOpen}
                          aria-haspopup="listbox"
                          aria-controls={dialListId}
                          onClick={() => setPhoneDialOpen((o) => !o)}
                        >
                          <ContactDialFlagImg
                            iso={dialMetaForCode(phoneDialCode).iso}
                            className="contact-modern-dial-trigger-flag-img"
                          />
                          <span className="contact-modern-dial-value">{phoneDialCode}</span>
                          <span className="contact-modern-dial-chevron" aria-hidden />
                        </button>
                        {phoneDialOpen ? (
                          <ul
                            id={dialListId}
                            className="contact-modern-dial-list"
                            role="listbox"
                            aria-label={t("contact.countryCodeAria")}
                          >
                            {CONTACT_DIAL_OPTIONS.map(({ code, iso }) => (
                              <li key={code} role="none">
                                <button
                                  type="button"
                                  role="option"
                                  className={
                                    phoneDialCode === code
                                      ? "contact-modern-dial-option is-active"
                                      : "contact-modern-dial-option"
                                  }
                                  aria-selected={phoneDialCode === code}
                                  onClick={() => {
                                    setPhoneDialCode(code);
                                    setPhoneDialOpen(false);
                                  }}
                                >
                                  <ContactDialFlagImg
                                    iso={iso}
                                    className="contact-modern-dial-option-flag-img"
                                  />
                                  <span className="contact-modern-dial-option-code">{code}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                      <input
                        type="tel"
                        className="contact-modern-phone-number"
                        name="phone"
                        autoComplete="tel-national"
                        inputMode="numeric"
                        placeholder={t("contact.phonePlaceholder")}
                        value={phone}
                        onChange={(e) =>
                          setPhone(e.target.value.replace(/[^\d\s]/g, ""))
                        }
                        aria-label={t("contact.phonePlaceholder")}
                      />
                    </span>
                  </label>
                  <label className="contact-modern-field">
                    <i className="fas fa-tag" aria-hidden />
                    <input
                      type="text"
                      name="subject"
                      placeholder={t("contact.subjectPlaceholder")}
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </label>
                </div>
                <label className="contact-modern-field contact-modern-field--message">
                  <i className="fas fa-pen" aria-hidden />
                  <textarea
                    name="message"
                    rows={4}
                    placeholder={t("contact.messagePlaceholder")}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </label>
                <button type="submit" className="contact-modern-submit" disabled={loading}>
                  {t("contact.ctaSubmit")}
                </button>
                <label className="contact-modern-consent" style={isRtl ? { direction: "rtl" } : undefined}>
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span className="contact-modern-consent-text">
                    {t("contact.consent")}
                    <a href="#privacy" className="contact-modern-consent-link">
                      {t("contact.consentLink")}
                    </a>
                  </span>
                </label>
              </form>
            </div>
          </div>
        </div>
      </section>
      {showConfirmation && (
        <div className="confirmation-modal confirmation-modal--home" role="dialog" aria-modal="true">
          <div className="modal-content sec contact-home-success-modal">
            <img src="/photos/done.svg" alt="" width={120} height={120} />
            <p className="contact-home-success-text">{t("popups.success")}</p>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowConfirmation(false)}>
                {t("popups.okay")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
export default Home;
